/**
 * useHashModal Hook
 * 
 * Manages modal state using URL hash fragments for better back button support.
 * Perfect for Android users who expect back button to close modals.
 * 
 * @example
 * ```tsx
 * const { isOpen, open, close, forceClose } = useHashModal('dialog-sign-in');
 * 
 * // In JSX:
 * <button onClick={open}>Sign In</button>
 * <SignInModal isOpen={isOpen} onClose={close} />
 * 
 * // After successful action (like login):
 * forceClose(); // Removes hash without navigation
 * ```
 * 
 * @module hooks
 */

import { useEffect, useState } from 'react';

export interface UseHashModalReturn {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Open the modal (sets hash) */
  open: () => void;
  /** Close the modal (removes hash using history.back) */
  close: () => void;
  /** Force close by directly removing hash (use for programmatic closes) */
  forceClose: () => void;
}

/**
 * Hook to manage modal state via URL hash fragments
 * 
 * @param hashKey - The hash identifier (without #), e.g., 'dialog-sign-in'
 * @returns Modal state and control functions
 */
export function useHashModal(hashKey: string): UseHashModalReturn {
  // Initialize state based on current hash
  const [isOpen, setIsOpen] = useState(() => {
    // Only check hash on client-side (not during SSR)
    if (typeof window === 'undefined') return false;
    const currentHash = window.location.hash.slice(1);
    return currentHash === hashKey;
  });

  // Set up hash change listener
  useEffect(() => {
    // Check if current hash matches our key
    const checkHash = () => {
      const currentHash = window.location.hash.slice(1); // Remove # prefix
      setIsOpen(currentHash === hashKey);
    };

    // Listen for hash changes (back/forward navigation, direct hash changes)
    window.addEventListener('hashchange', checkHash);

    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, [hashKey]);

  // Open modal by setting hash
  const open = () => {
    window.location.hash = hashKey;
  };

  // Close modal by removing hash (for user-initiated closes)
  const close = () => {
    // Only proceed if our hash is currently active
    if (window.location.hash === `#${hashKey}`) {
      // Use history.back() for better browser history integration
      // Safe because we verify the hash is our modal's hash above
      // This makes the back button work naturally and reverses the hash that opened the modal
      // Note: If modal was opened by this hook (via open()), history.back() safely closes it
      window.history.back();
    }
  };

  // Force close by directly removing hash (for programmatic closes like after login)
  const forceClose = () => {
    if (window.location.hash === `#${hashKey}`) {
      // Directly remove hash without going back in history
      // This prevents navigating away from current page
      history.replaceState(null, '', window.location.pathname + window.location.search);
      
      // Manually update state since replaceState doesn't trigger hashchange event
      setIsOpen(false);
    }
  };

  return { isOpen, open, close, forceClose };
}
